import { MapUtils } from '../../../../utility/map-utils.js';
import { VersionRange } from '../../../core/version-range.js';
import { cloneData, isSameData } from '../../../dto/data-model-utils.js';
import {
  SerializedData, SerializationContext, DataModelBuilder,
  DataModel,
} from '../../../dto/data-model.js';
import { GenericOrder } from '../../orders/generic-order.js';
import { AuditLogEntryType } from '../audit-log-entry-type.js';
import { AuditLogEntry } from '../audit-log-entry.js';

export class GenericOrderRefundedEntry extends AuditLogEntry {
  static readonly TYPE_ID: string = '4fd849b4749c4692bada91df6ab1e7e9';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly before: GenericOrder,
    public readonly after: GenericOrder,
    // If null the entire order was refunded. However it does not mean all
    // entries were modified, some might have already been refunded.
    // A full diff is required to get detailed info about what was refunded.
    public readonly orderEntryId: string | null,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderRefundedEntry.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GenericOrderRefundedEntry.SCHEMA_VERSION);
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.GenericOrderRefunded;
  }

  generateQueryData(): SerializedData {
    return {
      businessRef: this.after.businessRef.encode(),
      orderId: this.after.uniqueId,
      customerId: this.after.creatorId,
      isGuestOrder: this.after.isGuestOrder,
      orderEntryId: this.orderEntryId,
    };
  }

  static create(args: {
    before: GenericOrder,
    after: GenericOrder,
    orderEntryId: string | null,
  }): GenericOrderRefundedEntry {
    return new GenericOrderRefundedEntry(
        args.before,
        args.after,
        args.orderEntryId,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderRefundedEntry {
    return this.create({
      before: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'before')),
      after: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'after')),
      orderEntryId: MapUtils.getStringOrNull(map, 'orderEntryId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      before: context.toData(this.before),
      after: context.toData(this.after),
      orderEntryId: this.orderEntryId,
    };
  }

  clone(): this {
    return GenericOrderRefundedEntry.create({
      before: cloneData(this.before),
      after: cloneData(this.after),
      orderEntryId: this.orderEntryId,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.before, this.before) &&
           isSameData(other.after, this.after) &&
           other.orderEntryId === this.orderEntryId;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof GenericOrderRefundedEntry;
  }

  static assertType(dm: DataModel): dm is GenericOrderRefundedEntry {
    return dm instanceof GenericOrderRefundedEntry;
  }
}

export class GenericOrderRefundedEntryBuilder extends DataModelBuilder<GenericOrderRefundedEntry> {
  build(context: SerializationContext, map: SerializedData): GenericOrderRefundedEntry {
    return GenericOrderRefundedEntry.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GenericOrderRefundedEntry.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrderRefundedEntry.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `orderEntryId` of type `string | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
